import { useState, useRef, useEffect } from "react";
import { Trash2, X, ThumbsUp, MessageCircle, Send, Loader2, Check, MoreHorizontal, Edit2, CirclePlus, CircleMinus, BadgeCheck, Dot, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "../../Components/UI/dialog";
import { Drawer, DrawerContent } from "../../Components/UI/drawer";
import useIsMobile from "../../Hooks/useIsMobile";
import useHaptics from "../../Hooks/useHaptics";
import { Avatar, CategoryBadge, timeAgo, PostDetailSkeleton, isLikedByUser, isPostOwnedByCurrentUser, PostMediaGrid, PostCarousel } from "./CommunityShared";
import { addCommentAPI, editCommentAPI, deleteCommentAPI, getPostDetailsAPI } from "./FeedAPI";
import { getMyAgenciesMappedIDs, openInExternalBrowser } from "../../Utils/Utilities";
import { SHOW_VIEW_COUNTS_IN_FEED, COMMUNITY_ALLOW_MULTIPLE_COMMENTS } from "../../Utils/FeatureFlags";
import apiService from "../../Services/APIService";

// True if any of the user's MyAgencies profiles is that agency's admin (UserID === AdminUserID
// on that profile row) - an admin of even one agency gets to see every reply, same as an author.
function isAgencyAdminOfAny() {
  try {
    const raw = localStorage.getItem('MyAgencies');
    if (!raw) return false;
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return false;
    return list.some((item) => Number(item.UserID) > 0 && Number(item.UserID) === Number(item.AdminUserID));
  } catch (e) {
    console.error("Error in isAgencyAdminOfAny:", e);
    return false;
  }
}

// A viewer (not the post's author, not an agency admin) only sees replies posted by themselves
// or by the post's creator - checked across all of their MyAgencies profiles (same multi-agency
// "is this mine" pattern used elsewhere, e.g. getTaskCategoryIDForUser), not just the single
// currently-active UserID/AgencyID.
function getVisibleComments(comments, isAuthor, currentUser, post) {
  if (!comments) return [];

  const audTypeID = Number(post?.AudTypeID ?? post?.AudienceTypeID ?? 1);
  // If private comment (AudTypeID = 2): only the creator/author/admin and the commenting user can see their own
  if (audTypeID === 2 && !isAuthor && !isAgencyAdminOfAny()) {
    const myUserIDs = getMyAgenciesMappedIDs('UserID');
    const myAgencyIDs = getMyAgenciesMappedIDs('AgencyID');
    const myUserID = Number(currentUser?.UserID);
    const myAgencyID = Number(currentUser?.AgencyID);
    return comments.filter((cmt) => {
      const uid = Number(cmt.UID);
      const aid = Number(cmt.AID ?? cmt.AgencyID);
      return uid === myUserID || aid === myAgencyID || myUserIDs.includes(uid) || myAgencyIDs.includes(aid);
    });
  }

  // If no comments at all (AudTypeID = 3)
  if (audTypeID === 3 && !isAuthor && !isAgencyAdminOfAny()) {
    return [];
  }

  if (isAuthor || isAgencyAdminOfAny()) return comments;

  const myUserIDs = getMyAgenciesMappedIDs('UserID');
  const myAgencyIDs = getMyAgenciesMappedIDs('AgencyID');
  const myUserID = Number(currentUser?.UserID);
  const myAgencyID = Number(currentUser?.AgencyID);
  const creatorUserID = Number(post?.CreatedUserID);
  const creatorAgencyID = Number(post?.CreatedAgencyID);

  return comments.filter((cmt) => {
    const uid = Number(cmt.UID);
    const aid = Number(cmt.AID ?? cmt.AgencyID);
    return uid === myUserID || aid === myAgencyID || myUserIDs.includes(uid) || myAgencyIDs.includes(aid)
      || uid === creatorUserID || aid === creatorAgencyID;
  });
}

// Recursive helper to build nested comment tree from flat list, with sibling sorting
function buildCommentTree(comments) {
  if (!comments || !Array.isArray(comments)) return [];
  const map = new Map();
  const roots = [];

  // Normalize each item
  const list = comments.map((c) => ({
    ...c,
    CID: Number(c.CID ?? c.cid ?? 0),
    PID: Number(c.PID ?? c.pid ?? c.ParentCommentID ?? c.parentCommentId ?? 0),
    children: []
  }));

  list.forEach((node) => {
    if (node.CID) {
      map.set(node.CID, node);
    }
  });

  list.forEach((node) => {
    if (node.PID > 0 && map.has(node.PID)) {
      map.get(node.PID).children.push(node);
    } else {
      roots.push(node);
    }
  });

  // Sort siblings by CreatedDT ascending (or CID if no DT)
  const sortNodes = (nodes) => {
    nodes.sort((a, b) => {
      const timeA = a.DT ? new Date(a.DT).getTime() : a.CID;
      const timeB = b.DT ? new Date(b.DT).getTime() : b.CID;
      return timeA - timeB;
    });
    nodes.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);

  return roots;
}

// Compute total descendant count per node
function getDescendantCount(node) {
  if (!node.children || node.children.length === 0) return 0;
  return node.children.reduce((acc, curr) => acc + 1 + getDescendantCount(curr), 0);
}

function ReplyItem({
  cmt,
  parentCmt = null,
  parentAvatarSize = 28,
  isLastSibling = false,
  onAgencyClick,
  currentUser,
  onStartEdit,
  onDeleteComment,
  onStartReply,
  onCancelReply,
  editingCommentID,
  replyingToComment,
  collapsedMap,
  onToggleCollapse,
  level = 0,
  isMobile,
  inputRef,
  replyText,
  setReplyText,
  handleSendReply,
  sending
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showAllChildren, setShowAllChildren] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const menuRef = useRef(null);

  const isBeingEdited = editingCommentID != null && Number(cmt.CID) === Number(editingCommentID);
  const isBeingRepliedTo = replyingToComment != null && Number(cmt.CID) === Number(replyingToComment.CID);

  const myUserIDs = getMyAgenciesMappedIDs('UserID');
  const isDeleted = Boolean(cmt.IsD || cmt.isD || cmt.IsDeleted || cmt.isDeleted || cmt.Val === "[deleted]" || cmt.UName === "[deleted]");
  const isOwn = !isDeleted && cmt.CID && (Number(cmt.UID) === Number(currentUser?.UserID) || myUserIDs.includes(Number(cmt.UID)));
  const isCollapsed = Boolean(collapsedMap[cmt.CID]);
  const repliesCollapsed = Boolean(collapsedMap[`replies_${cmt.CID}`] ?? isCollapsed);
  const children = cmt.children || [];
  const childCount = getDescendantCount(cmt);

  // Depth clamping: max visual indent is 6 on desktop, 3 on mobile
  const maxIndent = isMobile ? 3 : 6;
  const isBeyondMaxDepth = level > maxIndent;
  const avatarSize = 28;
  const avatarCenter = 14; // Center of 28px avatar
  const threadIndent = 36; // Indent matching avatar + gap to author text
  const pad = 10;

  // Close menu on outside click
  useEffect(() => {
    if (!showActionsMenu) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowActionsMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showActionsMenu]);

  // Pagination for depth >= 2: show first 3 children unless expanded
  const shouldPaginate = level >= 2 && children.length > 3 && !showAllChildren;
  const visibleChildren = shouldPaginate ? children.slice(0, 3) : children;
  const hiddenChildrenCount = children.length - visibleChildren.length;
  const firstChild = children[0];
  const isFirstChildDeleted = Boolean(firstChild?.IsD || firstChild?.isD || firstChild?.IsDeleted || firstChild?.isDeleted || firstChild?.Val === "[deleted]" || firstChild?.UName === "[deleted]");

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await onDeleteComment(cmt);
    } finally {
      setDeleting(false);
      setConfirmingDelete(false);
      setShowActionsMenu(false);
    }
  };

  const hasVisibleChildren = !repliesCollapsed && children.length > 0;

  return (
    <div
      id={cmt.CID ? `comment-${cmt.CID}` : undefined}
      className="node relative"
      style={{
        paddingTop: `${pad}px`
      }}
    >
      {/* Elbow Connector from parent's vertical line to this comment's avatar */}
      {level > 0 && !isBeyondMaxDepth && (
        <>
          <div
            className="absolute pointer-events-none transition-colors"
            style={{
              left: `-${threadIndent - avatarCenter + 1}px`,
              top: 0,
              width: `${threadIndent - avatarCenter + 1}px`,
              height: `${pad + avatarCenter}px`,
              borderLeft: "2px solid var(--thread-line, var(--sahi-border-color))",
              borderBottom: "2px solid var(--thread-line, var(--sahi-border-color))",
              borderBottomLeftRadius: "8px"
            }}
            aria-hidden="true"
          />

          {/* Continuation line for non-last siblings so parent's line reaches down to subsequent siblings */}
          {!isLastSibling && (
            <div
              className="absolute pointer-events-none transition-colors"
              style={{
                left: `-${threadIndent - avatarCenter + 1}px`,
                top: `${pad + avatarCenter}px`,
                bottom: 0,
                width: "2px",
                backgroundColor: "var(--thread-line, var(--sahi-border-color))"
              }}
              aria-hidden="true"
            />
          )}
        </>
      )}

      {/* Header Row (Avatar, Name, Agency, Time, Collapse Toggle, Menu) */}
      <div
        className="flex items-center gap-2 min-w-0 flex-wrap"
        onClick={isCollapsed ? () => onToggleCollapse(cmt.CID) : undefined}
      >
        {!isDeleted && (
          <div
            className={`shrink-0 ${cmt.AgencyID && !isCollapsed ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
            onClick={(e) => {
              if (!isCollapsed && cmt.AgencyID) {
                e.stopPropagation();
                onAgencyClick?.(cmt.AgencyID);
              }
            }}
          >
            <Avatar
              src={cmt.ALogo || cmt.AgencyLogo || cmt.ULogo}
              uName={cmt.UName}
              aName={cmt.AName}
              size={avatarSize}
            />
          </div>
        )}
        <span
          onClick={(e) => {
            if (!isCollapsed) {
              e.stopPropagation();
              !isDeleted && cmt.AgencyID && onAgencyClick?.(cmt.AgencyID);
            }
          }}
          className={`text-[13px] font-semibold truncate ${isDeleted
            ? "text-sub-text-color italic"
            : `text-text-color ${cmt.AgencyID && !isCollapsed ? "cursor-pointer hover:text-sahi-primary-color hover:underline" : ""}`
            }`}
        >
          {isDeleted ? "[deleted]" : (cmt.UName || "Member")}
        </span>
        {!isDeleted && cmt.AName && (
          <span className="hidden sm:inline text-[12px] text-sub-text-color font-normal truncate max-w-[150px]">
            · {cmt.AName}
          </span>
        )}
        {cmt.DT && (
          <span className="text-[12px] text-sub-text-color font-normal shrink-0">
            · {timeAgo(cmt.DT)}
          </span>
        )}

        {/* Collapsed descendants / replies count */}
        {isCollapsed && childCount > 0 && (
          <span
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse(cmt.CID);
            }}
            className="text-[12px] text-sub-text-color font-normal shrink-0 cursor-pointer hover:underline"
          >
            · {childCount} {childCount === 1 ? "reply" : "replies"}
          </span>
        )}

        {/* Secondary Actions Menu (...) in Header */}
        {!isDeleted && isOwn && (
          <div className="relative ml-0.5" ref={menuRef}>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowActionsMenu((v) => !v); }}
              className="p-1 rounded-full hover:bg-sahi-disable/80 text-sub-text-color hover:text-text-color transition-colors cursor-pointer flex items-center justify-center border-0 bg-transparent"
              title="More options"
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>

            {showActionsMenu && !confirmingDelete && (
              <div className="absolute left-0 top-full mt-1 z-30 min-w-[110px] bg-sahi-white rounded-lg shadow-md border border-sahi-border-color py-1 text-xs font-semibold">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowActionsMenu(false);
                    onStartEdit(cmt);
                  }}
                  className="w-full px-3 py-1.5 text-left text-text-color hover:bg-sahi-disable flex items-center gap-2 cursor-pointer"
                >
                  <Edit2 className="h-3.5 w-3.5 text-sub-text-color" />
                  <span>Edit</span>
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmingDelete(true);
                  }}
                  className="w-full px-3 py-1.5 text-left text-sahi-red hover:bg-sahi-red/10 flex items-center gap-2 cursor-pointer"
                >
                  <Trash2 className="h-3.5 w-3.5 text-sahi-red" />
                  <span>Delete</span>
                </button>
              </div>
            )}

            {confirmingDelete && (
              <div className="absolute left-0 top-full mt-1 z-30 bg-sahi-white rounded-lg shadow-md border border-sahi-red/30 p-2 text-xs flex items-center gap-2 whitespace-nowrap">
                <span className="text-sahi-red font-medium">Delete reply?</span>
                <button
                  type="button"
                  disabled={deleting}
                  onClick={(e) => { e.stopPropagation(); handleConfirmDelete(); }}
                  className="font-bold text-sahi-red hover:underline disabled:opacity-40 cursor-pointer"
                >
                  {deleting ? "..." : "Yes"}
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setConfirmingDelete(false); setShowActionsMenu(false); }}
                  className="text-sub-text-color hover:underline cursor-pointer"
                >
                  No
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Comment Content (Indented under Header, text aligned with Author Name) */}
      <div
        className="content min-w-0 relative"
        style={{
          paddingLeft: isBeyondMaxDepth ? "0px" : `${threadIndent}px`,
          marginTop: "0px"
        }}
      >
        {/* Vertical Line through center of this comment's avatar down through replies */}
        {children.length > 0 && (
          <div
            onClick={(e) => { e.stopPropagation(); onToggleCollapse(`replies_${cmt.CID}`); }}
            className="group/line absolute top-0 bottom-0 cursor-pointer z-0 transition-colors"
            style={{
              left: `${avatarCenter - 1}px`,
              width: "2px",
              backgroundColor: "var(--thread-line, var(--sahi-border-color))"
            }}
            title={repliesCollapsed ? "Click to expand replies" : "Click to collapse replies"}
          >
            {/* Wider hover hit area */}
            <div className="absolute -left-[6px] top-0 bottom-0 w-[14px]" />

            {/* Minus indicator on the vertical line inline with Reply actions */}
            {!repliesCollapsed && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleCollapse(`replies_${cmt.CID}`); }}
                aria-expanded="true"
                aria-label="Collapse replies"
                className="absolute bottom-0 -left-[6px] w-[14px] h-7 flex items-center justify-center text-sub-text-color hover:text-sahi-primary-color transition-colors cursor-pointer z-10 p-0 border-0 bg-transparent"
                title="Collapse replies"
              >
                <CircleMinus size={14} strokeWidth={2} className="shrink-0 bg-sahi-white rounded-full" />
              </button>
            )}
          </div>
        )}

        {/* Replying-to label if beyond max visual depth */}
        {!isCollapsed && isBeyondMaxDepth && parentCmt && (
          <div className="mt-1 text-[11px] text-sub-text-color font-medium truncate">
            Replying to <span className="font-semibold text-text-color">@{parentCmt.UName || parentCmt.AName || "member"}</span>
          </div>
        )}

        {/* Comment Body & Actions */}
        {!isCollapsed && (
          <>
            {isDeleted ? (
              <p className="text-[13px] text-sub-text-color italic mt-1 bg-sahi-disable/40 px-2.5 py-1.5 rounded-lg border border-sahi-border-color/30 inline-block">
                Comment deleted by user
              </p>
            ) : (
              <p className="text-[14px] leading-[1.5] text-text-color mt-1 whitespace-pre-wrap [overflow-wrap:anywhere] selection:bg-sahi-primary-color/20">
                {cmt.Val}
              </p>
            )}

            {/* Actions Row & Reply Box (Only for non-deleted comments) */}
            {!isDeleted && (COMMUNITY_ALLOW_MULTIPLE_COMMENTS ? level < 3 : level < 1) && (
              <div className="mt-1 h-7 flex items-center gap-2 text-[12px] font-medium text-sub-text-color">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onStartReply(cmt); }}
                  className={`cursor-pointer transition-colors font-semibold leading-none flex items-center ${isBeingRepliedTo
                    ? "text-sahi-primary-color underline"
                    : "hover:text-text-color hover:underline"
                    }`}
                >
                  Reply
                </button>
              </div>
            )}

            {!isDeleted && (
              <>
                {/* Desktop Inline Reply Composer */}
                {!isMobile && isBeingRepliedTo && (
                  <div
                    className="mt-2 p-3 rounded-xl bg-sahi-white"
                    style={{
                      border: "1px solid color-mix(in srgb, var(--sahi-primary-color) 40%, transparent)",
                      boxShadow: "0 0 0 2px color-mix(in srgb, var(--sahi-primary-color) 18%, transparent)"
                    }}
                  >
                    <textarea
                      ref={inputRef}
                      rows={2}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendReply();
                        } else if (e.key === "Escape") {
                          onCancelReply();
                        }
                      }}
                      placeholder={`Reply to ${cmt.UName || "member"}...`}
                      className="w-full bg-transparent text-sm text-text-color placeholder:text-sub-text-color outline-none resize-none"
                    />
                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-sahi-border-color/50 mt-1.5">
                      <button
                        type="button"
                        onClick={onCancelReply}
                        className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-sub-text-color hover:bg-sahi-disable transition-colors cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        disabled={!replyText.trim() || sending}
                        onClick={handleSendReply}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-sahi-primary-color text-white hover:opacity-90 disabled:opacity-40 transition-opacity cursor-pointer flex items-center gap-1"
                      >
                        {sending ? <Loader2 size={13} className="animate-spin" /> : null}
                        <span>Reply</span>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Collapsed Replies Branch Preview */}
      {repliesCollapsed && children.length > 0 && (
        <div
          className="relative flex items-center gap-1.5 cursor-pointer group/collapsed hover:opacity-90 select-none py-1 transition-opacity"
          style={{
            paddingLeft: isBeyondMaxDepth ? "4px" : `${threadIndent + 4}px`,
            minHeight: "24px"
          }}
          onClick={(e) => {
            e.stopPropagation();
            onToggleCollapse(`replies_${cmt.CID}`);
          }}
        >
          {/* Elbow connector curve matching child indentation */}
          {!isBeyondMaxDepth && (
            <div
              className="absolute pointer-events-none transition-colors"
              style={{
                left: `${avatarCenter - 1}px`,
                top: 0,
                width: `${threadIndent - avatarCenter + 1}px`,
                height: "12px",
                borderLeft: "2px solid var(--thread-line, var(--sahi-border-color))",
                borderBottom: "2px solid var(--thread-line, var(--sahi-border-color))",
                borderBottomLeftRadius: "8px"
              }}
              aria-hidden="true"
            />
          )}

          {/* Plus Icon */}
          <CirclePlus
            size={14}
            strokeWidth={2}
            className="shrink-0 text-sub-text-color group-hover/collapsed:text-sahi-primary-color transition-colors"
          />

          {/* Child Metadata Preview */}
          <div className="flex items-center gap-1.5 min-w-0 flex-wrap text-[12px] text-sub-text-color">
            {firstChild && !isFirstChildDeleted ? (
              <>
                <span className="font-semibold text-text-color truncate max-w-[140px] group-hover/collapsed:text-sahi-primary-color transition-colors">
                  {firstChild.UName || "Member"}
                </span>
                {firstChild.AName && (
                  <span className="hidden sm:inline font-normal truncate max-w-[120px]">
                    · {firstChild.AName}
                  </span>
                )}
                {firstChild.DT && (
                  <span className="font-normal shrink-0">
                    · {timeAgo(firstChild.DT)}
                  </span>
                )}
              </>
            ) : null}

            <span className="font-medium text-sub-text-color shrink-0">
              · {childCount} {childCount === 1 ? "reply" : "replies"}
            </span>
          </div>
        </div>
      )}

      {/* Children Container: Sits below comment row, indented */}
      {!repliesCollapsed && children.length > 0 && (
        <div
          className="children relative"
          style={{
            paddingLeft: isBeyondMaxDepth ? "0px" : `${threadIndent}px`
          }}
        >
          {visibleChildren.map((child, idx) => (
            <ReplyItem
              key={child.CID}
              cmt={child}
              parentCmt={cmt}
              isLastSibling={idx === visibleChildren.length - 1 && (!shouldPaginate || hiddenChildrenCount === 0)}
              onAgencyClick={onAgencyClick}
              currentUser={currentUser}
              onStartEdit={onStartEdit}
              onDeleteComment={onDeleteComment}
              onStartReply={onStartReply}
              onCancelReply={onCancelReply}
              editingCommentID={editingCommentID}
              replyingToComment={replyingToComment}
              collapsedMap={collapsedMap}
              onToggleCollapse={onToggleCollapse}
              level={level + 1}
              isMobile={isMobile}
              inputRef={inputRef}
              replyText={replyText}
              setReplyText={setReplyText}
              handleSendReply={handleSendReply}
              sending={sending}
            />
          ))}

          {/* "N more replies" Row */}
          {shouldPaginate && hiddenChildrenCount > 0 && (
            <div
              className="relative flex items-center gap-2 pt-2 cursor-pointer text-sub-text-color hover:text-text-color transition-colors"
              onClick={() => setShowAllChildren(true)}
            >
              {/* Elbow connector to N more replies */}
              <div
                className="absolute pointer-events-none"
                style={{
                  left: `calc(-${threadIndent}px - 1px)`,
                  top: 0,
                  width: `${threadIndent}px`,
                  height: "18px",
                  borderLeft: "2px solid var(--thread-line, var(--sahi-border-color))",
                  borderBottom: "2px solid var(--thread-line, var(--sahi-border-color))",
                  borderBottomLeftRadius: "8px"
                }}
                aria-hidden="true"
              />
              <div className="w-4 h-4 rounded-full border border-sahi-border-color flex items-center justify-center text-[10px] font-bold bg-sahi-white shrink-0 z-10">
                +
              </div>
              <span className="text-[12px] font-semibold">
                {hiddenChildrenCount} more {hiddenChildrenCount === 1 ? "reply" : "replies"}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Post Detail Inner Content ──────────────────────────────────────────────
function PostDetailContent({
  post,
  isAuthor,
  onLikeToggle,
  onDeletePost,
  onReportPost,
  onClose,
  inputRef,
  replyText,
  setReplyText,
  sending,
  handleSendReply,
  onAgencyClick,
  currentUser,
  onStartEdit,
  onCancelEdit,
  editingCommentID,
  replyingToComment,
  onStartReply,
  onCancelReply,
  onDeleteComment,
  collapsedMap,
  onToggleCollapse,
  isMobile,
  initialImageIndex = 0
}) {
  const { vibrate } = useHaptics();
  const [photoError, setPhotoError] = useState(false);
  const [showFullPost, setShowFullPost] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    setPhotoError(false);
    setShowFullPost(false);
  }, [post?.PostID]);

  const liked = isLikedByUser(post, currentUser);
  const likeCount = Number(post?.CntLikes) || 0;

  const handleLike = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    vibrate("success");
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 400);
    onLikeToggle?.(post);
  };

  const handleVisitStore = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const agencyID = post?.CreatedAgencyID;
    if (agencyID) {
      if (onAgencyClick) {
        onAgencyClick(agencyID, post);
        onClose?.();
      } else {
        const webstoreUrl = `${window.location.origin}/web/store?m=${agencyID}`;
        openInExternalBrowser(webstoreUrl);
      }
    }
  };

  const visibleComments = getVisibleComments(post?.comments, isAuthor, currentUser, post);
  const commentTree = buildCommentTree(visibleComments);
  const replyCount = visibleComments.length || Number(post?.CntComments) || 0;
  const detail = post?.Detail || "";
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    if (textRef.current) {
      const el = textRef.current;
      setIsClamped(el.scrollHeight > el.clientHeight + 2);
    }
  }, [detail, post?.PostID]);

  const audTypeID = Number(post?.AudTypeID ?? post?.AudienceTypeID ?? 1);
  const isNoComments = audTypeID === 3 && !isAuthor && !isAgencyAdminOfAny();
  const city = (post?.City || post?.AgencyCity || post?.CreatedAgencyCity || post?.CreatedCity || "").trim();
  const state = (post?.State || post?.AgencyState || post?.CreatedAgencyState || post?.CreatedState || "").trim();
  const location = [city, state].filter(Boolean).join(", ");

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden bg-sahi-white">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3.5 border-b border-sahi-border-color shrink-0">
        <button
          onClick={handleVisitStore}
          className="shrink-0 hover:opacity-80 transition-opacity outline-none bg-transparent border-0 p-0 cursor-pointer"
        >
          <Avatar
            src={post?.CreatedAgencyLogo}
            aName={post?.CreatedAgencyName}
            size={42}
          />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0 text-sm">
            <button
              onClick={handleVisitStore}
              className="font-bold text-text-color hover:underline cursor-pointer outline-none bg-transparent border-0 p-0 text-left truncate"
            >
              {post?.CreatedAgencyName}
            </button>
            {post?.IsVerified && (
              <BadgeCheck
                size={14}
                className="text-sahi-blue fill-sahi-blue-light shrink-0"
              />
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-0.5 min-w-0 text-xs text-sub-text-color font-medium">
            {post?.CreatedUserName && (
              <span className="truncate max-w-[130px] sm:max-w-[180px]">
                {post.CreatedUserName}
              </span>
            )}
            {post?.CreatedUserName && location && (
              <Dot size={10} className="text-zinc-400 shrink-0 -mx-1" strokeWidth={5} />
            )}
            {location && (
              <span className="truncate max-w-[130px] sm:max-w-[180px]">
                {location}
              </span>
            )}
            {(post?.CreatedUserName || location) && post?.CreatedDT && (
              <Dot size={10} className="text-zinc-400 shrink-0 -mx-1" strokeWidth={5} />
            )}
            {post?.CreatedDT && (
              <span className="whitespace-nowrap">
                {timeAgo(post.CreatedDT)}
              </span>
            )}
            {SHOW_VIEW_COUNTS_IN_FEED && post?.CntViews !== undefined && (
              <>
                <Dot size={10} className="text-zinc-400 shrink-0 -mx-1" strokeWidth={5} />
                <span className="whitespace-nowrap">
                  {post?.CntViews || 0} {Number(post?.CntViews) === 1 ? "View" : "Views"}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-1.5 items-center shrink-0">
          {isAuthor && (
            <button
              onClick={() => onDeletePost(post)}
              className="p-1.5 text-sub-text-color hover:text-sahi-red sahi-radius transition-colors cursor-pointer"
              title="Delete Post"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onClose}
            className="p-1.5 text-sub-text-color hover:bg-sahi-disable sahi-radius transition-colors cursor-pointer"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Post Content */}
      <div className="flex-1 overflow-y-auto scrollbar-hide">
        {(() => {
          const rawPhotos = post?.Photos?.length ? post.Photos : (post?.Photos ? [post.Photos] : []);
          const photos = rawPhotos.filter((p) => {
            if (!p || typeof p !== "string") return false;
            const s = p.trim();
            if (!s || s === "[]" || s === "{}" || s === "null" || s === "undefined") return false;
            if (s.endsWith("/Uploads/") || s.endsWith("/Uploads") || s === "/Uploads/" || s === "/Uploads") return false;
            return true;
          });
          if (!photos.length || photoError) return null;

          return (
            <PostCarousel
              photos={photos}
              title={post?.Title}
              initialIndex={initialImageIndex}
            />
          );
        })()}

        <div className="p-4">
          {(post?.Type || post?.PostTypeID || post?.TypeID) && (
            <div className="mb-1.5 sm:mb-2">
              <CategoryBadge type={post?.Type || post?.PostTypeID || post?.TypeID} />
            </div>
          )}
          <h2 className="text-[16px] font-bold text-text-color mb-2 leading-tight">
            {post?.Title}
          </h2>
          <div
            ref={textRef}
            className={`text-[13px] sm:text-sm text-text-color leading-normal whitespace-pre-wrap ${!showFullPost ? "line-clamp-6" : ""}`}
          >
            {detail}
          </div>
          {(isClamped || showFullPost) && (
            <button
              onClick={() => setShowFullPost((v) => !v)}
              className="mt-2 text-xs font-bold text-sahi-primary-color hover:underline cursor-pointer"
            >
              {showFullPost ? "Show Less" : "Read More"}
            </button>
          )}
        </div>

        {/* Action Buttons matching PostCard */}
        <div className="px-4 py-2 border-y border-sahi-border-color bg-sahi-white">
          <style>{`
            @keyframes postLikeNudge {
              0% {
                transform: scale(1);
              }
              40% {
                transform: scale(1.45);
              }
              100% {
                transform: scale(1);
              }
            }
            .animate-post-like-nudge {
              animation: postLikeNudge 400ms cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
              transform-origin: center center;
              will-change: transform;
            }
          `}</style>
          <div className="flex items-center justify-between gap-2 w-full">
            <button
              onClick={handleLike}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 sahi-radius text-[13px] font-semibold transition-all duration-300 active:scale-95 cursor-pointer border-0 bg-transparent ${liked ? "text-sahi-green bg-[#0f925c15]" : "text-sub-text-color hover:bg-sahi-disable"}`}
            >
              <ThumbsUp
                size={16}
                strokeWidth={liked ? 2.5 : 2}
                className={`${liked ? "fill-[#0f925c22] text-sahi-green" : "text-sub-text-color"} ${isAnimating ? "animate-post-like-nudge" : "transition-colors duration-300"}`}
              />
              <span>
                {likeCount > 0 ? `${likeCount} ` : ""}
                {likeCount > 1 ? "Likes" : "Like"}
              </span>
            </button>

            <button
              onClick={() => inputRef?.current?.focus()}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 sahi-radius text-[13px] font-semibold text-sub-text-color hover:bg-sahi-disable transition-colors cursor-pointer border-0 bg-transparent"
            >
              <MessageCircle size={16} />
              <span>
                {!isNoComments && replyCount > 0 ? `${replyCount} ` : ""}
                {!isNoComments && replyCount > 1 ? "Replies" : "Reply"}
              </span>
            </button>

            <button
              onClick={handleVisitStore}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 sahi-radius text-[13px] font-semibold text-sub-text-color hover:bg-sahi-disable cursor-pointer border-0 bg-transparent transition-colors"
            >
              <ExternalLink size={16} />
              <span>Visit</span>
            </button>
          </div>
        </div>

        {/* Comments Section */}
        <div className="p-4 bg-sahi-white">
          {/* Header with single non-redundant title */}
          <div className="mb-1">
            <p className="text-[13px] font-bold text-text-color">
              Comments {isNoComments ? "" : visibleComments.length > 0 ? `· ${visibleComments.length}` : ""}
            </p>
          </div>

          {isNoComments ? (
            <div className="text-center py-6">
              <MessageCircle className="h-7 w-7 text-sahi-border-color mx-auto mb-1 opacity-50" />
              <p className="text-xs text-sub-text-color">Comments are disabled for this post.</p>
            </div>
          ) : commentTree.length > 0 ? (
            <div className="space-y-0">
              {commentTree.map((cmt) => (
                <ReplyItem
                  key={cmt.CID}
                  cmt={cmt}
                  onAgencyClick={onAgencyClick}
                  currentUser={currentUser}
                  onStartEdit={onStartEdit}
                  onDeleteComment={onDeleteComment}
                  onStartReply={onStartReply}
                  onCancelReply={onCancelReply}
                  editingCommentID={editingCommentID}
                  replyingToComment={replyingToComment}
                  collapsedMap={collapsedMap}
                  onToggleCollapse={onToggleCollapse}
                  level={0}
                  isMobile={isMobile}
                  inputRef={inputRef}
                  replyText={replyText}
                  setReplyText={setReplyText}
                  handleSendReply={handleSendReply}
                  sending={sending}
                />
              ))}
            </div>
          ) : (
            <div
              onClick={() => inputRef.current?.focus()}
              className="text-center py-6 cursor-pointer group select-none"
            >
              <MessageCircle className="h-7 w-7 text-sahi-border-color mx-auto mb-1 group-hover:text-sahi-primary-color transition-colors" />
              <p className="text-xs text-sub-text-color group-hover:text-sahi-primary-color transition-colors">Be the first to comment</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Composer (For top-level comments or mobile reply-to mode) */}
      <div className="p-3 border-t border-sahi-border-color bg-sahi-white flex flex-col gap-1.5">
        {isNoComments ? (
          <div className="text-center py-1 text-xs text-sub-text-color font-medium">
            Comments are turned off for this post.
          </div>
        ) : (
          <>
            {editingCommentID != null && (
              <div className="flex items-center justify-between text-xs text-sub-text-color font-semibold px-1">
                <span>Editing reply</span>
                <button onClick={onCancelEdit} className="text-sub-text-color hover:text-sahi-red cursor-pointer">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            {/* Mobile: Replying To Header above bottom dock */}
            {isMobile && replyingToComment != null && (
              <div className="flex items-center justify-between text-xs text-sahi-primary-color font-semibold px-2 py-1 bg-sahi-primary-color/5 rounded-md">
                <span>Replying to <span className="font-bold">{replyingToComment.UName || replyingToComment.AName || "Member"}</span></span>
                <button onClick={onCancelReply} className="text-sub-text-color hover:text-sahi-red cursor-pointer">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            <div className="flex items-end gap-2">
              <div className="flex-1 flex items-end gap-2 bg-sahi-white sahi-radius border border-sahi-border-color px-2 pl-3 py-2 focus-within:border-sahi-primary-color transition-colors">
                <textarea
                  ref={isMobile || !replyingToComment ? inputRef : null}
                  rows={1}
                  value={isMobile || !replyingToComment ? replyText : ""}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendReply();
                    } else if (e.key === "Escape") {
                      if (editingCommentID != null) onCancelEdit();
                      if (replyingToComment != null) onCancelReply();
                    }
                  }}
                  placeholder={
                    editingCommentID != null
                      ? "Edit your comment..."
                      : isMobile && replyingToComment != null
                        ? `Reply to ${replyingToComment.UName || "member"}...`
                        : "Add a comment..."
                  }
                  className="flex-1 bg-transparent text-[13px] text-text-color placeholder:text-sub-text-color outline-none resize-none py-1"
                />
                <button
                  disabled={!replyText.trim() || sending}
                  onClick={handleSendReply}
                  className="p-1.5 sahi-radius bg-sahi-primary-color text-white disabled:opacity-40 transition-transform active:scale-90 cursor-pointer"
                  title={editingCommentID != null ? "Save" : "Send"}
                >
                  {sending ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : editingCommentID != null ? (
                    <Check size={16} />
                  ) : (
                    <Send size={16} />
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Matches TaskCategoryManager.jsx's helper of the same name - keeps the drawer sized to
// the actual visible viewport (accounting for the mobile keyboard) rather than a fixed vh.
const useVisualViewportHeight = () => {
  const [height, setHeight] = useState(() => window.visualViewport ? window.visualViewport.height : window.innerHeight);

  useEffect(() => {
    if (!window.visualViewport) return;
    const handleResize = () => {
      setHeight(window.visualViewport.height);
    };
    window.visualViewport.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return height;
};

// ─── Post Detail Modal/Drawer Component ─────────────────────────────────────
export default function PostDetailModal({
  post,
  open,
  onClose,
  onAddReply,
  onLikeToggle,
  onDeletePost,
  onReportPost,
  currentUser,
  loading = false,
  onAgencyClick,
  focusCommentInput = false,
  initialImageIndex = 0
}) {
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [editingCommentID, setEditingCommentID] = useState(null);
  const [editingAgencyID, setEditingAgencyID] = useState(null);
  const [replyingToComment, setReplyingToComment] = useState(null);
  const [collapsedMap, setCollapsedMap] = useState({});
  const inputRef = useRef(null);
  const isMobile = useIsMobile();
  const { vibrate } = useHaptics();
  const vvHeight = useVisualViewportHeight();

  useEffect(() => {
    if (open && !loading) {
      if (focusCommentInput) {
        setTimeout(() => inputRef.current?.focus(), 250);
      }
    } else if (!open) {
      setReplyText("");
      setEditingCommentID(null);
      setEditingAgencyID(null);
      setReplyingToComment(null);
      setCollapsedMap({});
    }
  }, [open, isMobile, focusCommentInput, loading]);

  if (!open || !post) return null;

  // A comment posted "as" one of my other agency profiles has a UserID that only that agency
  // context resolves to - switch APIService's active agency for the duration of the call so the
  // server validates against the right UserID (same pattern Tasks.jsx uses for cross-agency edits).
  const runAsCommentOwner = async (agencyID, fn) => {
    const targetAgencyID = Number(agencyID || 0);
    if (targetAgencyID) apiService.setAgencyContext(targetAgencyID);
    try {
      return await fn();
    } finally {
      if (targetAgencyID) apiService.clearAgencyContext();
    }
  };

  const scrollToComment = (targetCID) => {
    if (!targetCID) return;
    const scroll = () => {
      const el = document.getElementById(`comment-${targetCID}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        return true;
      }
      return false;
    };

    // Try immediately after render frame, retry after short intervals if rendering or drawer transition takes time
    setTimeout(() => {
      if (!scroll()) {
        setTimeout(scroll, 100);
      }
    }, 50);
  };

  // Shared by add/edit/delete - re-fetches the post so comments/counts reflect the server's
  // latest state, then bubbles the result up to Community.jsx via onAddReply.
  const refreshComments = async () => {
    const { summary, comments } = await getPostDetailsAPI(post.PostID);
    const mappedComments = (comments || []).map((c) => ({
      CID: Number(c.CID ?? c.cid ?? 0),
      PID: Number(c.PID ?? c.pid ?? c.ParentCommentID ?? c.parentCommentId ?? 0),
      UID: Number(c.UID ?? c.uid ?? 0),
      UName: c.UName || c.uname || "",
      AName: c.AName || c.aname || "",
      AgencyID: c.AgencyID || c.agencyID || c.AID || c.aid || c.CreatedAgencyID || c.CreatedAID,
      Val: c.Val || c.val || "",
      DT: c.DT || c.dt || "",
      IsD: Boolean(c.IsD || c.isD || c.IsDeleted || c.isDeleted)
    }));
    onAddReply(post.PostID, mappedComments, summary?.CntViews, summary?.CntComments);
    return mappedComments;
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    vibrate("success");
    setSending(true);

    try {
      if (editingCommentID != null) {
        const targetCID = editingCommentID;
        await runAsCommentOwner(editingAgencyID, () => editCommentAPI(post.PostID, editingCommentID, replyText.trim()));
        await refreshComments();
        setEditingCommentID(null);
        setEditingAgencyID(null);
        setReplyText("");
        scrollToComment(targetCID);
      } else {
        const parentCommentID = replyingToComment ? Number(replyingToComment.CID || 0) : 0;
        const existingCIDs = new Set((post?.comments || []).map((c) => Number(c.CID)));
        await addCommentAPI(post.PostID, replyText.trim(), parentCommentID);
        const updatedComments = await refreshComments();
        setReplyText("");
        setReplyingToComment(null);

        // Find the newly created comment ID (newest CID not in previous comments or max CID)
        const newComment = (updatedComments || []).find((c) => !existingCIDs.has(Number(c.CID))) ||
          (updatedComments || []).reduce((max, c) => (!max || c.CID > max.CID ? c : max), null);

        if (newComment?.CID) {
          scrollToComment(newComment.CID);
        }
      }
    } catch (error) {
      console.error("Error saving reply:", error);
      toast.error(editingCommentID != null ? "This reply could not be updated." : "This reply could not be posted.", {
        description: error?.message || "Please try again or contact support if the issue persists.",
        duration: 4000
      });
    } finally {
      setSending(false);
    }
  };

  const handleStartEdit = (cmt) => {
    setReplyingToComment(null);
    setEditingCommentID(cmt.CID);
    setEditingAgencyID(cmt.AgencyID || null);
    setReplyText(cmt.Val);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleCancelEdit = () => {
    setEditingCommentID(null);
    setEditingAgencyID(null);
    setReplyText("");
  };

  const handleStartReply = (cmt) => {
    setEditingCommentID(null);
    setEditingAgencyID(null);
    setReplyingToComment(cmt);
    // Auto-expand if collapsed
    if (cmt.CID && collapsedMap[cmt.CID]) {
      setCollapsedMap((prev) => ({ ...prev, [cmt.CID]: false }));
    }
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleCancelReply = () => {
    setReplyingToComment(null);
  };

  const handleToggleCollapse = (cid) => {
    setCollapsedMap((prev) => ({
      ...prev,
      [cid]: !prev[cid]
    }));
  };

  const handleDeleteComment = async (cmt) => {
    try {
      await runAsCommentOwner(cmt.AgencyID, () => deleteCommentAPI(post.PostID, cmt.CID));
      if (editingCommentID === cmt.CID) {
        setEditingCommentID(null);
        setEditingAgencyID(null);
        setReplyText("");
      }
      if (replyingToComment?.CID === cmt.CID) {
        setReplyingToComment(null);
      }
      await refreshComments();
    } catch (error) {
      console.error("Error deleting reply:", error);
      toast.error("This reply could not be deleted.", {
        description: error?.message || "Please try again or contact support if the issue persists.",
        duration: 4000
      });
    }
  };

  const isAuthor = isPostOwnedByCurrentUser(post, currentUser);

  // Rounding to a whole pixel avoids the fractional maxHeight (e.g. 741.15px) that was making
  // the nested overflow-y-auto content render on a sub-pixel boundary and look blurry compared
  // to the header/footer (which aren't clipped/scrolled and so weren't affected).
  const drawerMaxHeight = Math.round(vvHeight * 0.9);

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DrawerContent
          className="p-0 [&>div:first-child]:hidden top-auto rounded-t-[22px] overflow-hidden !h-auto"
          style={{
            maxHeight: `${drawerMaxHeight}px`
          }}
        >
          <div className="flex flex-col flex-1 min-h-0 overflow-hidden" style={{ maxHeight: `${drawerMaxHeight}px` }}>
            {loading ? (
              <PostDetailSkeleton />
            ) : (
              <PostDetailContent
                post={post}
                isAuthor={isAuthor}
                onLikeToggle={onLikeToggle}
                onDeletePost={onDeletePost}
                onReportPost={onReportPost}
                onClose={onClose}
                inputRef={inputRef}
                replyText={replyText}
                setReplyText={setReplyText}
                sending={sending}
                handleSendReply={handleSendReply}
                onAgencyClick={onAgencyClick}
                currentUser={currentUser}
                onStartEdit={handleStartEdit}
                onCancelEdit={handleCancelEdit}
                editingCommentID={editingCommentID}
                replyingToComment={replyingToComment}
                onStartReply={handleStartReply}
                onCancelReply={handleCancelReply}
                onDeleteComment={handleDeleteComment}
                collapsedMap={collapsedMap}
                onToggleCollapse={handleToggleCollapse}
                isMobile={isMobile}
                initialImageIndex={initialImageIndex}
              />
            )}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()} preventOutsideClick={true}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] p-0 flex flex-col overflow-hidden shadow-2xl border border-sahi-border-color rounded-2xl">
        {loading ? (
          <PostDetailSkeleton />
        ) : (
          <PostDetailContent
            post={post}
            isAuthor={isAuthor}
            onLikeToggle={onLikeToggle}
            onDeletePost={onDeletePost}
            onReportPost={onReportPost}
            onClose={onClose}
            inputRef={inputRef}
            replyText={replyText}
            setReplyText={setReplyText}
            sending={sending}
            handleSendReply={handleSendReply}
            onAgencyClick={onAgencyClick}
            currentUser={currentUser}
            onStartEdit={handleStartEdit}
            onCancelEdit={handleCancelEdit}
            editingCommentID={editingCommentID}
            replyingToComment={replyingToComment}
            onStartReply={handleStartReply}
            onCancelReply={handleCancelReply}
            onDeleteComment={handleDeleteComment}
            collapsedMap={collapsedMap}
            onToggleCollapse={handleToggleCollapse}
            isMobile={isMobile}
            initialImageIndex={initialImageIndex}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
